import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/button";
import { Text } from "~/components/text";
import { useAnalytics } from "~/contexts/analytics-context";
import type { SeasonalEventSurface } from "~/contexts/analytics-context/analytics-events";
import type { SeasonalEvent } from "~/integrations/firebase/collections/seasonal-events";
import { CalendarEventRow } from "./calendar-event-row";
import {
  buildCalendarMonthGroups,
  formatMonthGroupLabel,
  INITIAL_MONTHS_SHOWN,
  MONTHS_REVEAL_STEP,
  type CalendarEventEntry,
} from "./calendar-utils";
import { useSeasonalToday } from "./hooks/use-seasonal-today";

interface SeasonalEventCalendarProps {
  events: SeasonalEvent[];
  surface: SeasonalEventSurface;
  onSelectEvent: (entry: CalendarEventEntry) => void;
}

export const SeasonalEventCalendar = ({
  events,
  surface,
  onSelectEvent,
}: SeasonalEventCalendarProps): ReactElement => {
  const { t } = useTranslation(["components"]);
  const { gaEvent } = useAnalytics();
  const [monthsShown, setMonthsShown] = useState(INITIAL_MONTHS_SHOWN);
  const pendingFocusMonthKeyRef = useRef<string | null>(null);
  const monthSectionByKeyRef = useRef(new Map<string, HTMLElement>());
  const today = useSeasonalToday();

  const monthGroups = useMemo(
    () => buildCalendarMonthGroups(events, today),
    [events, today],
  );
  const visibleGroups = monthGroups.slice(0, monthsShown);
  const hasMoreMonths = monthsShown < monthGroups.length;

  useEffect(() => {
    gaEvent({
      type: "calendar_opened",
      payload: { entry_point: "view_calendar", surface },
    });
  }, [gaEvent, surface]);

  useEffect(() => {
    const monthKey = pendingFocusMonthKeyRef.current;
    if (monthKey === null) {
      return;
    }
    pendingFocusMonthKeyRef.current = null;

    const frame = requestAnimationFrame(() => {
      monthSectionByKeyRef.current.get(monthKey)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [monthsShown]);

  const handleSeeMore = (): void => {
    const nextShown = Math.min(
      monthsShown + MONTHS_REVEAL_STEP,
      monthGroups.length,
    );
    if (nextShown >= monthGroups.length) {
      pendingFocusMonthKeyRef.current =
        monthGroups[monthsShown]?.monthKey ?? null;
    }
    setMonthsShown(nextShown);
    gaEvent({
      type: "calendar_more_months_loaded",
      payload: { months_shown: nextShown },
    });
  };

  return (
    // min-w-0: as a grid item of the dialog, this column's minimum width is
    // otherwise the longest event name (nowrap under truncate) and overflows.
    <div className="flex min-w-0 flex-col gap-4">
      <Text as="p" variant="muted">
        {t("components:seasonalEventModal.calendar.intro")}
      </Text>

      <div className="max-h-[min(28rem,60vh)] overflow-y-auto">
        <div className="flex flex-col gap-5">
          {visibleGroups.map((group) => {
            const headingId = `seasonal-calendar-month-${group.monthKey}`;
            return (
              <section
                key={group.monthKey}
                aria-labelledby={headingId}
                tabIndex={-1}
                className="flex flex-col gap-2"
                ref={(node) => {
                  if (node) {
                    monthSectionByKeyRef.current.set(group.monthKey, node);
                  } else {
                    monthSectionByKeyRef.current.delete(group.monthKey);
                  }
                }}
              >
                <Text as="h3" id={headingId} variant="overline">
                  {formatMonthGroupLabel(group.monthKey)}
                </Text>
                <ul className="flex flex-col gap-2">
                  {group.entries.map((entry) => (
                    <li
                      key={`${entry.event.event_id}-${entry.monthKey}-${entry.occurrence.start}-${entry.occurrence.end}-${entry.occurrence.date_kind}`}
                    >
                      <CalendarEventRow
                        entry={entry}
                        onSelect={onSelectEvent}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </div>

      {hasMoreMonths ? (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="tertiary"
            size="small"
            className="rounded-full"
            icon="chevron-down"
            label={t("components:seasonalEventModal.calendar.seeMore")}
            onClick={handleSeeMore}
          />
        </div>
      ) : null}
    </div>
  );
};
